/**
 * UI-8c Task 7 — RotationHistoryDrawer.
 *
 * Read-only side drawer that lists recent `sox_control_log` entries for a
 * single tenant's DEK-rotation control (the `SOX-KR` control ref). Each row
 * shows:
 *
 *   - `formatDateTime(recordedAt)` — when the rotation completed
 *   - actor      — 'system' for the SYSTEM_ACTOR uuid, else first-8 of the
 *                  actor's user id (full id is in the SOX log if needed)
 *   - evidence   — collapsible JSON via the shared SoxEvidenceCell
 *
 * State:
 *   - `entries === null` → fetch is in-flight, show "Loading history…"
 *   - `entries === []`   → empty state ("No rotations yet.")
 *   - otherwise          → ordered list of entries
 *
 * The drawer chrome is supplied by the shared `ResourceDrawer` so this
 * component focuses on the list shape. ResourceDrawer requires an
 * `onSave` prop; we supply a no-op since this is a read-only view.
 */
import { useEffect, useState, type JSX } from 'react'

import ResourceDrawer from '../admin/ResourceDrawer.js'
import { SoxEvidenceCell } from '../admin/compliance/SoxEvidenceCell.js'
import {
  useKeyRotation,
  type SoxEntry,
} from '../../hooks/useKeyRotation.js'
import { formatDateTime } from '../../utils/formatDate.js'

export interface RotationHistoryDrawerProps {
  open: boolean
  tenantSlug: string
  onClose: () => void
}

/** Shared sentinel uuid for system-actor SOX entries. */
const SYSTEM_ACTOR = '00000000-0000-0000-0000-000000000000'

function formatActor(actorId: string): string {
  if (actorId === SYSTEM_ACTOR) return 'system'
  return actorId.slice(0, 8)
}

export function RotationHistoryDrawer({
  open,
  tenantSlug,
  onClose,
}: RotationHistoryDrawerProps): JSX.Element {
  const { history } = useKeyRotation()
  const [entries, setEntries] = useState<SoxEntry[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !tenantSlug) {
      setEntries(null)
      setError(null)
      return
    }
    let cancelled = false
    setEntries(null)
    setError(null)
    void history(tenantSlug)
      .then((r) => {
        if (!cancelled) setEntries(r.entries)
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : 'Failed to load rotation history',
          )
          setEntries([])
        }
      })
    return () => {
      cancelled = true
    }
  }, [open, tenantSlug, history])

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      title={`History: ${tenantSlug}`}
      width="wide"
      onSave={async () => {}}
    >
      {error && (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
      {entries === null ? (
        <p className="text-sm text-gray-500">Loading history…</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-gray-500">No rotations yet.</p>
      ) : (
        <ul className="space-y-3">
          {entries.map((e) => (
            <li
              key={e.id}
              className="rounded border border-gray-200 p-3 text-sm"
              data-testid={`rotation-entry-${e.id}`}
            >
              <div className="flex flex-wrap items-baseline gap-3">
                <span className="font-medium text-gray-800">
                  {formatDateTime(e.recordedAt)}
                </span>
                <span className="text-xs text-gray-500">
                  actor: {formatActor(e.actorId)}
                </span>
                <span className="text-xs text-gray-500">
                  status: {e.status}
                </span>
              </div>
              <div className="mt-1">
                <SoxEvidenceCell evidence={e.evidence} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </ResourceDrawer>
  )
}

export default RotationHistoryDrawer
